import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { MapPin, Globe, X, Info, ShieldCheck, Plus } from 'lucide-react';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
];

const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany',
  'France', 'Netherlands', 'Singapore', 'UAE', 'Saudi Arabia', 'Qatar',
  'New Zealand', 'Ireland', 'Switzerland', 'Sweden', 'Norway', 'Denmark',
  'Belgium', 'Austria', 'Italy', 'Spain', 'Portugal', 'Japan', 'South Korea',
  'Malaysia', 'Thailand', 'Indonesia', 'Philippines', 'Vietnam', 'South Africa',
  'Kenya', 'Nigeria', 'Egypt', 'Brazil', 'Mexico', 'Argentina', 'Chile'
];

/* ─────────────────────────────────────────────────────────────────────────
 * Product-specific: Delivery Coverage (state-based with tier banners)
 * ───────────────────────────────────────────────────────────────────────── */
const PRODUCT_COVERAGE_OPTIONS = [
  { value: 'my_state', label: 'Only my State', description: 'Sell within your registered state only' },
  { value: 'selected_states', label: 'Selected States', description: 'Choose specific states to deliver to' },
  { value: 'all_india', label: 'All India', description: 'Available across India' },
  { value: 'all_india_exclusions', label: 'All India with Exclusions', description: 'Pan-India except specific areas' },
  { value: 'international', label: 'International', description: 'Available worldwide' },
];

const TIER_BANNERS = {
  2: {
    color: 'bg-amber-50 border-amber-200', iconColor: 'text-amber-600', textColor: 'text-amber-700',
    title: 'Tier 2 — Intra-State Sales Only',
    body: (state) => `Your State GST limits sales to ${state ? `within ${state}` : 'your registered state'}. Upgrade to Tier 3 for pan-India selling.`,
  },
  3: {
    color: 'bg-blue-50 border-blue-200', iconColor: 'text-blue-600', textColor: 'text-blue-700',
    title: 'Tier 3 — Pan-India Sales',
    body: 'You can sell anywhere in India. International selling requires Tier 4 (GST + IEC).',
  },
  6: {
    color: 'bg-purple-50 border-purple-200', iconColor: 'text-purple-600', textColor: 'text-purple-700',
    title: 'Tier 6 — International Seller',
    body: 'You can sell worldwide. Local and international buyers can interact with your listings.',
  },
};

const getTierNumber = (tierStr) => {
  if (!tierStr) return 1;
  const match = String(tierStr).match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 1;
};

const isOptionLocked = (value, tierNum) => {
  if (tierNum === 2) return !['my_state'].includes(value);
  if (tierNum === 3) return ['international'].includes(value);
  return false;
};

/* ─────────────────────────────────────────────────────────────────────────
 * Non-Product: Serviceable Area (cities, pincodes, countries, radius)
 * ───────────────────────────────────────────────────────────────────────── */
const SERVICE_COVERAGE_OPTIONS = [
  { value: 'all_india', label: 'All India', description: 'Available across India' },
  { value: 'selected_states', label: 'Selected States', description: 'Choose specific states' },
  { value: 'selected_cities', label: 'Selected Cities', description: 'Choose specific cities' },
  { value: 'pincodes', label: 'Specific Pincodes', description: 'Enter pincode ranges' },
  { value: 'selected_countries', label: 'Selected Countries', description: 'International markets' },
  { value: 'worldwide', label: 'Worldwide', description: 'No geographic restrictions' },
];

/* ─────────────────────────────────────────────────────────────────────────
 * Product Delivery Coverage — state radio group with tier gating
 * ───────────────────────────────────────────────────────────────────────── */
const ProductDeliveryCoverage = ({ serviceableArea, updateServiceableArea, tierNum, vendorState }) => {
  const [stateSearch, setStateSearch] = useState('');

  const getFallbackCoverage = () => {
    const saved = serviceableArea.coverage_type || 'all_india';
    if (tierNum === 2 && saved !== 'my_state') return 'my_state';
    if (tierNum === 3 && saved === 'international') return 'all_india';
    return saved;
  };

  const effectiveCoverage = getFallbackCoverage();

  const handleCoverageChange = (type) => {
    if (isOptionLocked(type, tierNum)) return;
    updateServiceableArea({
      coverage_type: type,
      states: type === 'my_state' && vendorState ? [vendorState] : [],
      exclusions: '',
    });
  };

  const toggleState = (state) => {
    const current = serviceableArea.states || [];
    updateServiceableArea({
      states: current.includes(state) ? current.filter(s => s !== state) : [...current, state],
    });
  };

  const filteredStates = INDIAN_STATES.filter(s => s.toLowerCase().includes(stateSearch.toLowerCase()));
  const selectedStates = serviceableArea.states || [];

  return (
    <>
      {TIER_BANNERS[tierNum] && (
        <div className={`p-3 border rounded-lg flex items-start gap-3 ${TIER_BANNERS[tierNum].color}`}>
          {tierNum === 6
            ? <ShieldCheck className={`h-4 w-4 flex-shrink-0 mt-0.5 ${TIER_BANNERS[tierNum].iconColor}`} />
            : <Info className={`h-4 w-4 flex-shrink-0 mt-0.5 ${TIER_BANNERS[tierNum].iconColor}`} />
          }
          <div className="text-sm">
            <p className={`font-semibold ${TIER_BANNERS[tierNum].textColor}`}>{TIER_BANNERS[tierNum].title}</p>
            <p className={`mt-0.5 ${TIER_BANNERS[tierNum].textColor}`}>
              {typeof TIER_BANNERS[tierNum].body === 'function' ? TIER_BANNERS[tierNum].body(vendorState) : TIER_BANNERS[tierNum].body}
            </p>
          </div>
        </div>
      )}

      <RadioGroup value={effectiveCoverage} onValueChange={handleCoverageChange} className="space-y-2" data-testid="coverage-type-radio">
        {PRODUCT_COVERAGE_OPTIONS.map((opt) => {
          const locked = isOptionLocked(opt.value, tierNum);
          return (
            <label
              key={opt.value}
              className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors
                ${effectiveCoverage === opt.value ? 'border-indigo-400 bg-indigo-50/50' : 'border-gray-200 hover:border-gray-300'}
                ${locked ? 'opacity-40 cursor-not-allowed' : ''}`}
              data-testid={`coverage-option-${opt.value}`}
            >
              <RadioGroupItem value={opt.value} disabled={locked} id={`coverage-${opt.value}`} className="mt-0.5" />
              <div>
                <span className="font-medium text-sm text-gray-900">{opt.label}</span>
                <p className="text-xs text-gray-500 mt-0.5">{opt.description}</p>
              </div>
            </label>
          );
        })}
      </RadioGroup>

      {effectiveCoverage === 'my_state' && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="my-state-info">
          <p className="text-sm text-green-700 flex items-center gap-2">
            <MapPin className="h-4 w-4 flex-shrink-0" />
            Delivering within {vendorState || 'your registered state'} only.
          </p>
        </div>
      )}

      {effectiveCoverage === 'selected_states' && (
        <div className="space-y-3" data-testid="selected-states-section">
          <Label>Select States</Label>
          <Input placeholder="Search states..." value={stateSearch} onChange={(e) => setStateSearch(e.target.value)} className="border-gray-300" id="state-search" name="state-search" data-testid="state-search-input" />
          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg bg-gray-50">
            {filteredStates.map((state) => (
              <Badge key={state} variant={selectedStates.includes(state) ? 'default' : 'outline'}
                className={`cursor-pointer transition-colors text-xs ${selectedStates.includes(state) ? 'bg-indigo-600 hover:bg-indigo-700' : 'hover:bg-gray-100'}`}
                onClick={() => toggleState(state)} data-testid={`state-badge-${state.replace(/\s+/g, '-').toLowerCase()}`}>
                {state}{selectedStates.includes(state) && <X className="h-3 w-3 ml-1" />}
              </Badge>
            ))}
          </div>
          {selectedStates.length > 0 && <p className="text-xs text-gray-500">{selectedStates.length} state{selectedStates.length !== 1 ? 's' : ''} selected</p>}
        </div>
      )}

      {effectiveCoverage === 'all_india' && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="all-india-info">
          <p className="text-sm text-green-700 flex items-center gap-2"><MapPin className="h-4 w-4 flex-shrink-0" />Your listing will be available to customers across all of India.</p>
        </div>
      )}

      {effectiveCoverage === 'all_india_exclusions' && (
        <div className="space-y-3" data-testid="exclusions-section">
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-700 flex items-center gap-2"><MapPin className="h-4 w-4 flex-shrink-0" />Available across India, except areas listed below.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="exclusions-textarea">Excluded Areas</Label>
            <Textarea id="exclusions-textarea" name="exclusions" placeholder={'Enter excluded States, Cities, or Pincodes (one per line or comma-separated)\n\nExample:\nJammu and Kashmir\nLeh, Ladakh\n799001, 799002'}
              value={serviceableArea.exclusions || ''} onChange={(e) => updateServiceableArea({ exclusions: e.target.value })} rows={5} className="border-gray-300" data-testid="exclusions-textarea" />
          </div>
        </div>
      )}

      {effectiveCoverage === 'international' && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="international-info">
          <p className="text-sm text-blue-700 flex items-center gap-2"><Globe className="h-4 w-4 flex-shrink-0" />Your listing will be available to customers worldwide.</p>
        </div>
      )}
    </>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
 * Non-Product Serviceable Area — dropdown + cities/pincodes/countries
 * ───────────────────────────────────────────────────────────────────────── */
const ServiceServiceableArea = ({ serviceableArea, updateServiceableArea, isTier2Locked, vendorState }) => {
  const effectiveCoverage = isTier2Locked ? 'selected_states' : (serviceableArea.coverage_type || 'all_india');
  const effectiveStates = isTier2Locked && vendorState ? [vendorState] : (serviceableArea.states || []);
  const [cityInput, setCityInput] = useState('');
  const [pincodeInput, setPincodeInput] = useState('');
  const [stateSearch, setStateSearch] = useState('');

  const handleCoverageChange = (type) => {
    if (isTier2Locked && type !== 'selected_states') return;
    updateServiceableArea({ coverage_type: type, states: type === 'selected_states' ? effectiveStates : [], cities: [], pincodes: [], countries: [] });
  };

  const toggleState = (state) => {
    if (isTier2Locked) return;
    const cur = serviceableArea.states || [];
    updateServiceableArea({ states: cur.includes(state) ? cur.filter(s => s !== state) : [...cur, state] });
  };

  const toggleCountry = (country) => {
    const cur = serviceableArea.countries || [];
    updateServiceableArea({ countries: cur.includes(country) ? cur.filter(c => c !== country) : [...cur, country] });
  };

  const addCity = () => {
    if (cityInput.trim()) {
      const cur = serviceableArea.cities || [];
      if (!cur.includes(cityInput.trim())) updateServiceableArea({ cities: [...cur, cityInput.trim()] });
      setCityInput('');
    }
  };

  const addPincode = () => {
    if (pincodeInput.trim() && /^\d{6}$/.test(pincodeInput.trim())) {
      const cur = serviceableArea.pincodes || [];
      if (!cur.includes(pincodeInput.trim())) updateServiceableArea({ pincodes: [...cur, pincodeInput.trim()] });
      setPincodeInput('');
    }
  };

  const filteredStates = INDIAN_STATES.filter(s => s.toLowerCase().includes(stateSearch.toLowerCase()));

  return (
    <>
      {isTier2Locked && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-amber-800">State GST Restriction</p>
            <p className="text-amber-700">Tier 2 KYC limits sales to <strong>{vendorState || 'your registered state'}</strong>. Upgrade to Tier 3 for nationwide sales.</p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label>Coverage Type</Label>
        <Select value={effectiveCoverage} onValueChange={handleCoverageChange} disabled={isTier2Locked}>
          <SelectTrigger data-testid="coverage-type-select"><SelectValue placeholder="Select coverage area" /></SelectTrigger>
          <SelectContent>
            {SERVICE_COVERAGE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} disabled={isTier2Locked && opt.value !== 'selected_states'}>
                <div className="flex flex-col"><span>{opt.label}</span><span className="text-xs text-gray-500">{opt.description}</span></div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {effectiveCoverage === 'selected_states' && (
        <div className="space-y-3">
          <Label>Select States</Label>
          {!isTier2Locked && <Input placeholder="Search states..." value={stateSearch} onChange={(e) => setStateSearch(e.target.value)} className="mb-2" />}
          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg bg-gray-50">
            {(isTier2Locked ? effectiveStates : filteredStates).map((state) => (
              <Badge key={state} variant={effectiveStates.includes(state) ? 'default' : 'outline'}
                className={`cursor-pointer transition-colors ${effectiveStates.includes(state) ? 'bg-indigo-600 hover:bg-indigo-700' : 'hover:bg-gray-100'} ${isTier2Locked ? 'cursor-not-allowed' : ''}`}
                onClick={() => toggleState(state)}>
                {state}{effectiveStates.includes(state) && !isTier2Locked && <X className="h-3 w-3 ml-1" />}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {effectiveCoverage === 'selected_cities' && (
        <div className="space-y-3">
          <Label>Add Cities</Label>
          <div className="flex gap-2">
            <Input placeholder="Enter city name" value={cityInput} onChange={(e) => setCityInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCity())} data-testid="city-input" />
            <Button type="button" onClick={addCity} variant="outline" size="sm"><Plus className="h-4 w-4" /></Button>
          </div>
          {(serviceableArea.cities || []).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {(serviceableArea.cities || []).map((city) => (
                <Badge key={city} variant="secondary" className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />{city}
                  <button type="button" onClick={() => updateServiceableArea({ cities: (serviceableArea.cities || []).filter(c => c !== city) })} className="ml-1 hover:text-red-600"><X className="h-3 w-3" /></button>
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      {effectiveCoverage === 'pincodes' && (
        <div className="space-y-3">
          <Label>Add Pincodes</Label>
          <div className="flex gap-2">
            <Input placeholder="Enter 6-digit pincode" value={pincodeInput} onChange={(e) => setPincodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPincode())} maxLength={6} data-testid="pincode-input" />
            <Button type="button" onClick={addPincode} variant="outline" size="sm"><Plus className="h-4 w-4" /></Button>
          </div>
          <p className="text-xs text-gray-500">Enter individual pincodes where your service is available</p>
          {(serviceableArea.pincodes || []).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {serviceableArea.pincodes.map((pin) => (
                <Badge key={pin} variant="secondary" className="flex items-center gap-1">
                  {pin}
                  <button type="button" onClick={() => updateServiceableArea({ pincodes: (serviceableArea.pincodes || []).filter(p => p !== pin) })} className="ml-1 hover:text-red-600"><X className="h-3 w-3" /></button>
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      {effectiveCoverage === 'selected_countries' && (
        <div className="space-y-3">
          <Label>Select Countries</Label>
          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg bg-gray-50">
            {COUNTRIES.map((country) => (
              <Badge key={country} variant={(serviceableArea.countries || []).includes(country) ? 'default' : 'outline'}
                className={`cursor-pointer transition-colors ${(serviceableArea.countries || []).includes(country) ? 'bg-indigo-600 hover:bg-indigo-700' : 'hover:bg-gray-100'}`}
                onClick={() => toggleCountry(country)}>
                {country}{(serviceableArea.countries || []).includes(country) && <X className="h-3 w-3 ml-1" />}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {effectiveCoverage === 'all_india' && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-700 flex items-center gap-2"><MapPin className="h-4 w-4" />Your service will be available to customers across all of India</p>
        </div>
      )}

      {effectiveCoverage === 'worldwide' && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700 flex items-center gap-2"><Globe className="h-4 w-4" />Your service will be available worldwide with no geographic restrictions</p>
        </div>
      )}
    </>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
 * Main Component — routes to Product or Service variant based on listingType
 * ───────────────────────────────────────────────────────────────────────── */
const ensureArray = (val) => Array.isArray(val) ? val : [];

const ServiceableAreaCard = ({ formData, handleChange, kycTier = null, vendorState = null, listingType = 'product' }) => {
  const raw = formData.serviceableArea || {};
  const serviceableArea = {
    coverage_type: raw.coverage_type || 'all_india',
    states: ensureArray(raw.states),
    cities: ensureArray(raw.cities),
    pincodes: ensureArray(raw.pincodes),
    countries: ensureArray(raw.countries),
    exclusions: raw.exclusions || '',
  };

  const tierNum = getTierNumber(kycTier);
  const isTier2Locked = kycTier === 'tier2' || tierNum === 2;
  const isProduct = listingType === 'product';

  const updateServiceableArea = (updates) => {
    handleChange('serviceableArea', { ...serviceableArea, ...updates });
  };

  return (
    <Card data-testid="serviceable-area-card">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-indigo-600" />
          <CardTitle className="text-lg">{isProduct ? 'Delivery Coverage' : 'Serviceable Area'}</CardTitle>
        </div>
        <CardDescription>
          {isProduct
            ? 'Where can you deliver this product?'
            : 'Define where your service is available. Customers outside this area will see an inquiry form instead of direct booking.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {isProduct ? (
          <ProductDeliveryCoverage
            serviceableArea={serviceableArea}
            updateServiceableArea={updateServiceableArea}
            tierNum={tierNum}
            vendorState={vendorState}
          />
        ) : (
          <ServiceServiceableArea
            serviceableArea={serviceableArea}
            updateServiceableArea={updateServiceableArea}
            isTier2Locked={isTier2Locked}
            vendorState={vendorState}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ServiceableAreaCard;
